import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getMenuAvailability, updateMenuAvailability } from '../../../shared/api/availability.js'

interface Props {
  organizationId: string
  menuId: string
  menuArchived: boolean
  canManage: boolean
}

export default function MenuAvailabilitySection({ organizationId, menuId, menuArchived, canManage }: Props) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [items, setItems] = useState<{
    locationId: string
    locationName: string
    isActive: boolean
    availableFrom: string | null
    availableUntil: string | null
  }[]>([])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  const readOnly = !canManage || menuArchived

  async function load() {
    setLoading(true)
    setError('')
    try {
      const avail = await getMenuAvailability(organizationId, menuId)
      if (avail) setItems(avail)
      setDirty(false)
    } catch (err: unknown) {
      setError(t('products.availabilityLoadError'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [organizationId, menuId])

  function setItemField(locationId: string, field: string, value: unknown) {
    setItems(prev => prev.map(item =>
      item.locationId === locationId ? { ...item, [field]: value } : item,
    ))
    setDirty(true)
    setSaveError('')
  }

  async function handleSave() {
    if (!dirty || saving) return
    setSaving(true)
    setSaveError('')
    try {
      const payload = items.map(i => ({
        locationId: i.locationId,
        isActive: i.isActive,
        availableFrom: i.availableFrom,
        availableUntil: i.availableUntil,
      }))
      const result = await updateMenuAvailability(organizationId, menuId, { availability: payload })
      setItems(result)
      setDirty(false)
    } catch { // eslint-disable-line @typescript-eslint/no-unused-vars
      setSaveError(t('products.availabilitySaveError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="settings-card">
      <div className="availability-header">
        <div>
          <h4>{t('menu.availability')}</h4>
          <p className="page-section-description">{t('menu.availability')}</p>
        </div>
      </div>

      {loading && <div className="page-loading"><div className="spinner" /></div>}

      {error && <div className="form-error-banner">{error}</div>}
      {saveError && <div className="form-error-banner">{saveError}</div>}

      {!loading && !error && items.length === 0 && (
        <p className="page-section-description">{t('products.availabilityNoLocations')}</p>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="availability-list">
          {items.map(item => (
            <div key={item.locationId} className="availability-menu-row">
              <label className="availability-row">
                <input
                  type="checkbox"
                  checked={item.isActive}
                  disabled={readOnly || saving}
                  onChange={e => setItemField(item.locationId, 'isActive', e.target.checked)}
                />
                <span className={item.isActive ? 'availability-on' : 'availability-off'}>
                  {item.isActive ? t('products.availabilityActive') : t('products.availabilityInactive')}
                </span>
                <span className="availability-location-name">{item.locationName}</span>
              </label>

              <div className="availability-dates">
                <label className="availability-date-field">
                  <span>{t('products.availableFrom')}</span>
                  <input
                    type="datetime-local"
                    value={item.availableFrom ? item.availableFrom.slice(0, 16) : ''}
                    disabled={readOnly || saving || !item.isActive}
                    onChange={e => {
                      const val = e.target.value
                      setItemField(item.locationId, 'availableFrom', val ? `${val}:00.000Z` : null)
                    }}
                  />
                </label>

                <label className="availability-date-field">
                  <span>{t('products.availableUntil')}</span>
                  <input
                    type="datetime-local"
                    value={item.availableUntil ? item.availableUntil.slice(0, 16) : ''}
                    disabled={readOnly || saving || !item.isActive}
                    onChange={e => {
                      const val = e.target.value
                      setItemField(item.locationId, 'availableUntil', val ? `${val}:00.000Z` : null)
                    }}
                  />
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      {dirty && !readOnly && (
        <div className="form-actions">
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void handleSave()}>
            {saving ? t('products.saving') : t('products.save')}
          </button>
        </div>
      )}
    </div>
  )
}